<template>
  <form @submit.prevent="next" novalidate class="w-full max-w-7xl" :aria-labelledby="t('checkout.headingId')">
    <h2 id="checkout-heading" class="sr-only">{{ t("checkout.heading") }}</h2>

    <section :aria-label="t('checkout.section.customerPaymentDetails')" class="space-y-4">
      <div class="space-y-4 sm:space-y-5">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
          <div>
            <label for="firstname" class="block text-sm sm:text-base font-medium mb-1">
              {{ t("checkout.form.firstnameLabel") }}
            </label>
            <input
              v-model="form.firstname"
              id="firstname"
              name="firstname"
              type="text"
              @blur="validateInput('firstname')"
              :class="inputClass(errors.firstname)"
              aria-describedby="error-firstname"
              class="mt-1 block w-full border rounded-lg px-3 py-3 text-base focus:ring focus:ring-offset-1" />
            <p v-if="errors.firstname" id="error-firstname" class="text-red-600 text-sm">
              {{ errors.firstname }}
            </p>
          </div>

          <div>
            <label for="lastname" class="block text-sm sm:text-base font-medium mb-1">
              {{ t("checkout.form.lastnameLabel") }}
            </label>
            <input
              v-model="form.lastname"
              id="lastname"
              name="lastname"
              type="text"
              @blur="validateInput('lastname')"
              :class="inputClass(errors.lastname)"
              aria-describedby="error-lastname"
              class="mt-1 block w-full border rounded-lg px-3 py-3 text-base focus:ring focus:ring-offset-1" />
            <p v-if="errors.lastname" id="error-lastname" class="text-red-600 text-sm">
              {{ errors.lastname }}
            </p>
          </div>
        </div>

        <div>
          <label for="address1" class="block text-sm sm:text-base font-medium mb-1">
            {{ t("checkout.form.addressLabel") }}
          </label>
          <input
            v-model="form.address1"
            id="address1"
            name="address1"
            type="text"
            @focus="initAddressAutocomplete"
            @blur="validateInput('address1')"
            :class="inputClass(errors.address1)"
            aria-describedby="error-address1"
            class="mt-1 block w-full border rounded-lg px-3 py-3 text-base focus:ring focus:ring-offset-1" />
          <p v-if="errors.address1" id="error-address1" class="text-red-600 text-sm">
            {{ errors.address1 }}
          </p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          <div>
            <label for="postalcode" class="block text-sm sm:text-base font-medium mb-1">
              {{ t("checkout.form.postalCodeLabel") }}
            </label>
            <input
              v-model="form.postalcode"
              id="postalcode"
              name="postalcode"
              type="text"
              @blur="validateInput('postalcode')"
              :class="inputClass(errors.postalcode)"
              aria-describedby="error-postalcode"
              class="mt-1 block w-full border rounded-lg px-3 py-3 text-base focus:ring focus:ring-offset-1" />
            <p v-if="errors.postalcode" id="error-postalcode" class="text-red-600 text-sm">
              {{ errors.postalcode }}
            </p>
          </div>

          <div>
            <label for="city" class="block text-sm sm:text-base font-medium mb-1">
              {{ t("checkout.form.cityLabel") }}
            </label>
            <input
              v-model="form.city"
              id="city"
              name="city"
              type="text"
              @blur="validateInput('city')"
              :class="inputClass(errors.city)"
              aria-describedby="error-city"
              class="mt-1 block w-full border rounded-lg px-3 py-3 text-base focus:ring focus:ring-offset-1" />
            <p v-if="errors.city" id="error-city" class="text-red-600 text-sm">
              {{ errors.city }}
            </p>
          </div>

          <div>
            <label for="country" class="block text-sm sm:text-base font-medium mb-1">
              {{ t("checkout.form.countryLabel") }}
            </label>
            <select
              v-model="form.country"
              id="country"
              name="country"
              @blur="validateInput('country')"
              :class="inputClass(errors.country)"
              aria-describedby="error-country"
              class="mt-1 block w-full border rounded-lg px-3 py-3 text-base focus:ring focus:ring-offset-1">
              <option value="">{{ t("checkout.form.selectCountryOption") }}</option>
              <option v-for="c in countries" :key="c.code" :value="c.code">
                {{ c.name }}
              </option>
            </select>
          </div>
        </div>

        <div>
          <label for="company" class="block text-sm sm:text-base font-medium mb-1">
            {{ t("checkout.form.companyLabel") }}
            <span class="text-gray-500 text-sm"> ({{ t("checkout.form.optional") }}) </span>
          </label>
          <input
            v-model="form.company"
            id="company"
            name="company"
            type="text"
            class="mt-1 block w-full border rounded-lg px-3 py-3 text-base focus:ring focus:ring-offset-1" />
        </div>

        <div>
          <label for="kvk" class="block text-sm sm:text-base font-medium mb-1">
            {{ t("checkout.form.kvkLabel") }}
            <span class="text-gray-500 text-sm"> ({{ t("checkout.form.optional") }}) </span>
          </label>
          <input
            v-model="form.kvk"
            id="kvk"
            name="kvk"
            type="text"
            class="mt-1 block w-full border rounded-lg px-3 py-3 text-base focus:ring focus:ring-offset-1" />
        </div>

        <div>
          <label for="vat" class="block text-sm sm:text-base font-medium mb-1">
            {{ t("checkout.form.vatLabel") }}
            <span class="text-gray-500 text-sm"> ({{ t("checkout.form.optional") }}) </span>
          </label>
          <input
            v-model="form.vat"
            id="vat"
            name="vat"
            type="text"
            class="mt-1 block w-full border rounded-lg px-3 py-3 text-base focus:ring focus:ring-offset-1" />
        </div>
      </div>

      <!-- Legal -->
      <div class="space-y-2">
        <label class="inline-flex items-start text-sm sm:text-base leading-relaxed">
          <input
            v-model="form.tos"
            type="checkbox"
            required
            @change="validateInput('tos')"
            class="mt-1 mr-3 h-4 w-4 sm:h-5 sm:w-5 focus:ring focus:ring-offset-1 flex-shrink-0" />
          <span>
            {{ t("checkout.form.legalAgreementPrefix") }}
            <a href="/legal/terms-and-conditions" target="_blank" class="text-blue-600 underline">
              {{ t("checkout.form.termsLink") }}
            </a>
            {{ t("checkout.form.legalAgreementMiddle") }}
            <a href="/legal/privacy-policy" target="_blank" class="text-blue-600 underline">
              {{ t("checkout.form.privacyLink") }} </a
            >.
          </span>
        </label>
        <p v-if="errors.tos" class="text-red-600 text-sm">
          {{ errors.tos }}
        </p>
      </div>

      <!-- Submit with countdown and error handling -->
      <div class="relative">
        <AcceptedPaymentsSection />

        <button
          :disabled="!canContinue || isSubmitting"
          type="submit"
          class="mt-6 w-full py-4 sm:py-3 rounded-lg shadow text-base sm:text-lg font-semibold transition-colors touch-manipulation"
          :class="[
            canContinue && !isSubmitting
              ? 'bg-primary text-white hover:shadow-lg cursor-pointer'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed opacity-60',
          ]">
          <span v-if="isSubmitting">
            {{ t("checkout.form.processing") }}
            <span v-if="countdown > 0"> ({{ t("checkout.form.processingCountdown", { count: countdown }) }}) </span>
          </span>
          <span v-else>
            {{ t("checkout.form.payNow") }}
          </span>
        </button>

        <ul v-if="!canContinue && !isSubmitting" class="text-sm text-gray-500 text-center mt-2 space-y-1">
          <li v-for="reason in blockingReasons" :key="reason">
            {{ reason }}
          </li>
        </ul>

        <p v-if="paymentError" class="text-error text-sm text-center mt-2">
          {{ paymentError }}
        </p>
      </div>
    </section>
  </form>
</template>

<script setup lang="ts">
import { onMounted, computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { defineProps } from "vue";
import { useAddressAutocomplete } from "@/composables/useAddressAutocomplete";
import { useCheckoutViewModel } from "../dtos/useCheckoutViewModel";
import { useCheckoutStore } from "../stores/checkoutStore";
import { validateCheckoutFormClientInformation } from "@@@/validators/checkoutValidators.js";
import AcceptedPaymentsSection from "@/components/sections/accepted-payments/AcceptedPaymentsSection.vue";

// i18n
const { t } = useI18n();

// props
const { billingInterval } = defineProps<{ billingInterval: "monthly" | "yearly" }>();

// stores & viewmodel
const checkoutStore = useCheckoutStore();
const { form, errors, validateStepClientInformation, validateInput, inputClass } = checkoutStore;

// countries with localized names
const countries = computed(() => [
  { code: "NL", name: t("countries.NL") },
  { code: "BE", name: t("countries.BE") },
]);

// address autocomplete
const { init: initAddressAutocomplete } = useAddressAutocomplete(
  "address1",
  form as unknown as {
    address1: string;
    postalcode: string;
    city: string;
    country: string;
  },
  countries.value
);
onMounted(() => initAddressAutocomplete());

// submission state
const isSubmitting = ref(false);
const countdown = ref(10);
const paymentError = ref("");
let countdownTimer: ReturnType<typeof setInterval> | null = null;
let failsafeTimer: ReturnType<typeof setTimeout> | null = null;

// blocking reasons
const blockingReasons = computed(() => {
  const reasons: string[] = [];
  if (!checkoutStore.form.email?.trim()) reasons.push(t("checkout.blocking.emailRequired"));
  if (checkoutStore.currentStep < 2) reasons.push(t("checkout.blocking.completeStep1"));
  const validationErrors = validateCheckoutFormClientInformation(form, t);
  reasons.push(...Object.values(validationErrors).filter(Boolean));
  return reasons;
});

const canContinue = computed(() => {
  // Check if basic required fields are filled without triggering validation
  if (!checkoutStore.form.email || checkoutStore.currentStep < 2) return false;
  
  // Check if required fields have values (no validation errors triggered)
  return !!(
    checkoutStore.form.firstname.trim() &&
    checkoutStore.form.lastname.trim() &&
    checkoutStore.form.address1.trim() &&
    checkoutStore.form.postalcode.trim() &&
    checkoutStore.form.city.trim() &&
    checkoutStore.form.country.trim() &&
    checkoutStore.form.tos
  );
});

// proceed to payment
async function next() {
  if (!validateStepClientInformation()) return;

  isSubmitting.value = true;
  paymentError.value = "";
  countdown.value = 10;

  countdownTimer = setInterval(() => {
    if (countdown.value > 0) countdown.value--;
  }, 1000);

  failsafeTimer = setTimeout(() => {
    isSubmitting.value = false;
    paymentError.value = t("checkout.payment.longProcessingError");
    clearInterval(countdownTimer!);
  }, 20000);

  try {
    const redirectUrl = await checkoutStore.submitPayment("mollieideal", billingInterval);
    clearTimeout(failsafeTimer!);
    clearInterval(countdownTimer!);
    window.location.href = redirectUrl;
  } catch {
    isSubmitting.value = false;
    paymentError.value = t("checkout.payment.startError");
    clearTimeout(failsafeTimer!);
    clearInterval(countdownTimer!);
  }
}
</script>
